import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useOnboardingStatus } from './useOnboarding'
import { useOnboardingGuidedStore, readPersistedGuidedSession } from '../../../store/onboardingGuidedStore'
import { useAuthStore } from '../../../store/authStore'
import {
  completedOnboardingKeys,
  isGuidedOnboardingSearch,
  nextGuidedStepKey,
} from '../../../lib/onboarding/onboardingGuided'
import { isOnboardingForfeited } from '../../../lib/onboarding/onboardingForfeitCache'
import { parseOnboardingStepParam } from '../../../lib/onboarding/onboardingOfferManagementGating'
import {
  getSidebarModuleLockState,
  isOnboardingJourneyActive,
  type SidebarModuleLockState,
} from '../../../lib/onboarding/onboardingSidebarGating'
import type { OnboardingStepKey } from '../../../config/onboardingRegistry'
import { isOnboardingGuideCoreComplete } from '../../../lib/onboarding/onboardingGuideConfig'

/**
 * Sidebar + OM journey locks.
 *
 * - journeyLocksActive: checklist visible, mid-guide — locks modules / bulk actions
 * - currentStep / completedKeys: shared progress context
 */
export function useOnboardingSidebarGating() {
  const { t } = useTranslation('dashboard')
  const [searchParams] = useSearchParams()
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const guidedActiveStore = useOnboardingGuidedStore((s) => s.active)
  const currentStepStore = useOnboardingGuidedStore((s) => s.currentStep)
  const { data: onboardingStatus, isFetched } = useOnboardingStatus()
  const forfeited = isOnboardingForfeited(onboardingStatus)

  const completedKeys = useMemo(
    () => completedOnboardingKeys(onboardingStatus),
    [onboardingStatus],
  )

  const journeyLocksActive = useMemo(() => {
    if (isAdmin) return false
    if (forfeited) return false
    if (!isOnboardingJourneyActive(onboardingStatus, completedKeys)) return false
    if (isOnboardingGuideCoreComplete(completedKeys)) return false
    return true
  }, [completedKeys, forfeited, isAdmin, onboardingStatus])

  /** Session or URL revival — used only to resolve currentStep when journey locks already apply. */
  const sessionOrUrlActive = useMemo(() => {
    if (isAdmin || forfeited || onboardingStatus?.all_completed) return false
    if (isOnboardingGuideCoreComplete(completedKeys)) return false
    const persisted = readPersistedGuidedSession()
    const fromSession = guidedActiveStore || persisted.active
    const fromUrl = isFetched && isGuidedOnboardingSearch(searchParams.toString())
    return fromSession || fromUrl
  }, [
    completedKeys,
    forfeited,
    guidedActiveStore,
    isAdmin,
    isFetched,
    onboardingStatus?.all_completed,
    searchParams,
  ])

  const gatingActive = journeyLocksActive || sessionOrUrlActive

  const currentStep = useMemo((): OnboardingStepKey | null => {
    const persisted = readPersistedGuidedSession()
    const explicit =
      currentStepStore ??
      persisted.currentStep ??
      parseOnboardingStepParam(searchParams.toString())
    if (explicit) return explicit
    if (gatingActive) {
      return nextGuidedStepKey(completedKeys)
    }
    return null
  }, [completedKeys, currentStepStore, gatingActive, searchParams])

  const getModuleLock = useCallback(
    (moduleId: string): SidebarModuleLockState & { tooltip: string | null } => {
      const state = getSidebarModuleLockState(
        moduleId,
        journeyLocksActive,
        completedKeys,
        currentStep,
      )
      if (!state.locked) {
        return { ...state, tooltip: null }
      }
      if (state.postGuideLock) {
        return {
          ...state,
          tooltip: t('onboarding.guided.lockedUntilGuideComplete'),
        }
      }
      const tooltip = state.unlockAfterStep
        ? t('onboarding.guided.lockedAfter', {
            step: t(`onboarding.steps.${state.unlockAfterStep}.title`),
          })
        : t('onboarding.guided.lockedDefault')
      return { ...state, tooltip }
    },
    [completedKeys, currentStep, journeyLocksActive, t],
  )

  return {
    journeyLocksActive,
    currentStep,
    completedKeys,
    getModuleLock,
  }
}
